import { useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { NsProductCard, NsColumn } from '@britishgasenergy/react-nucleus';

import { NsContent } from 'components/common/ns-content/NsContent';
import { SelectTariffCta } from 'components/direct-sales/quote/components/SelectTariffCta';
import { TariffHighlight } from 'components/direct-sales/quote/components/TariffHighlight';
import { TariffFeatures } from 'components/direct-sales/quote/components/TariffCards/TariffFeatures';
import { RateBreakDown } from 'components/direct-sales/quote/components/TariffCards/RatesBreakDown';
import { FUEL_TYPES } from 'config/app-config';
import { EmailQuoteCta } from 'components/direct-sales/quote/components/TariffCards/EmailQuoteCta';
import { TariffPill } from 'components/direct-sales/quote/components/TariffPill';
import { useTariffConfig } from 'components/direct-sales/quote/hooks/useTariffConfig';
import { useIsLoginEnabledJourney } from 'hooks/useIsLoginEnabledJourney';
import { TariffStandout } from 'components/direct-sales/quote/components/TariffCards/TariffStandout';
import { TariffPrice } from 'components/direct-sales/quote/components/TariffCards/TariffPrice';
import { pushEvent } from 'state/analytics/analytics-slice';

import type { TProduct } from 'api/models/TProduct';

export const testId = 'tariffCardTestId';

export type Props = {
  product?: TProduct;
};

export const TariffCard = ({ product = {} }: Props) => {
  const dispatch = useDispatch();
  const { name, monthlyAmount, annualAmount, gasTil, electricityTil } = product;
  const isMultirate = !!(
    electricityTil?.unitRateDay && electricityTil?.unitRateNight
  );
  const { decoration } = useTariffConfig(product);
  const isAgentJourney = useIsLoginEnabledJourney();

  const showMore = useMemo(
    () => ({
      'open-label': 'More details',
      'close-label': 'Hide tariff details',
      open: false
    }),
    []
  );

  const onToggle = useCallback(
    (event: CustomEvent) => {
      const { open } = event.detail;
      const action = open ? 'open' : 'close';
      const heading = open ? showMore['open-label'] : showMore['close-label'];

      dispatch(
        pushEvent({
          category: 'accordion',
          actionName: action,
          label: heading
        })
      );
    },
    [dispatch, showMore]
  );

  return useMemo(
    () => (
      <NsProductCard
        role="listitem"
        type="horizontal"
        showMore={showMore}
        decoration={decoration}
        onToggle={onToggle}
      >
        <TariffPill product={product} />

        <h2 slot="name">{name}</h2>

        <div slot="price-heading">
          <p className="p-caption">Estimated cost (inc. VAT)</p>
        </div>

        <div slot="price">
          <TariffPrice
            monthlyAmount={monthlyAmount}
            annualAmount={annualAmount}
          />
        </div>

        <div slot="description">
          <TariffHighlight product={product} />
          <TariffFeatures product={product} />
        </div>

        {!isAgentJourney && (
          <NsContent slot="details">
            <TariffStandout product={product} />
            <NsColumn auto>
              <RateBreakDown
                til={gasTil}
                fuel={FUEL_TYPES.GAS}
                fillUnitRate={isMultirate}
              />
              <RateBreakDown til={electricityTil} fuel={FUEL_TYPES.ELECTRIC} />
            </NsColumn>
          </NsContent>
        )}

        <NsContent slot="cta">
          <SelectTariffCta product={product}>Pick this tariff</SelectTariffCta>
          <EmailQuoteCta product={product} />
        </NsContent>
      </NsProductCard>
    ),
    [
      showMore,
      decoration,
      onToggle,
      product,
      name,
      monthlyAmount,
      annualAmount,
      isAgentJourney,
      gasTil,
      isMultirate,
      electricityTil
    ]
  );
};
